import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { API } from "@/lib/site";
import { setSeo } from "@/lib/seo";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSeo({
      title: "Insights | Blue Shuck Consulting LTD",
      description:
        "Practical insights on construction estimating, digital take-off, and software for construction SMEs from Blue Shuck Consulting LTD.",
    });
    window.scrollTo(0, 0);
    axios
      .get(`${API}/blog/posts`)
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="App bg-white min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-32 pb-24">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
          <Reveal>
            <Link
              data-testid="blog-back-home"
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-brand-navy transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to home
            </Link>
            <p className="mt-6 text-xs font-semibold tracking-[0.2em] uppercase text-brand-blue">
              Insights
            </p>
            <h1 className="mt-3 font-heading font-800 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-navy">
              Notes on construction software
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
              Practical thinking on estimating, digital take-off and building
              useful tools for construction SMEs.
            </p>
          </Reveal>

          {loading ? (
            <div className="mt-20 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
            </div>
          ) : posts.length === 0 ? (
            <p className="mt-16 text-slate-500">No posts yet. Check back soon.</p>
          ) : (
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, i) => (
                <Reveal key={post.slug} delay={i * 0.08} className="h-full">
                  <Link
                    data-testid={`blog-card-${post.slug}`}
                    to={`/blog/${post.slug}`}
                    className="group flex h-full flex-col rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wide text-brand-blue">
                        <span>{post.category}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-slate-400 normal-case font-medium tracking-normal">
                          {post.read_time}
                        </span>
                      </div>
                      <h2 className="mt-3 font-heading font-700 text-xl tracking-tight text-brand-navy group-hover:text-brand-blue transition-colors">
                        {post.title}
                      </h2>
                      <p className="mt-3 text-sm leading-relaxed text-slate-600 flex-1">
                        {post.excerpt}
                      </p>
                      <div className="mt-5 flex items-center justify-between">
                        <span className="text-xs text-slate-400">
                          {formatDate(post.published_at)}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-navy group-hover:gap-2 transition-all">
                          Read
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
